import { CallToActionLink, ButtonSize, ButtonColour } from "@trussle/external-components";
import { Link } from "gatsby";
import React, { useState } from "react";
import { ICalculatorResults } from "../../types/ICalculatorResults";
import { RichText } from "../RichText";
import "./styles.scss";
import PanelPlusSVG from "./panel-plus.svg";
import PanelMinusSVG from "./panel-minus.svg";
import { getCostPerItem, getNumericValue } from "../../helpers/calculatorsMethods";

export const HRule: React.FunctionComponent<any> = (props: any) => (
  // eslint-disable-next-line react/destructuring-assignment
  <div className={`horizontal-rule ${props.cssClasses}`}>
    {/* eslint-disable-next-line react/destructuring-assignment */}
    {props.children}
  </div>
);

export const currencyFormatter = new Intl.NumberFormat("en-GB", {
  currency: "GBP",
  style: "currency",
});

export const CalculatorResults: React.FunctionComponent<ICalculatorResults> = (
  props: ICalculatorResults,
) => {
  const {
    top: { title, result, subTitle },
    middle,
    bottomTitle,
    bottom,
    cta: { title: ctaText, url, handleClick: ctaHandleClick },
    secondCta,
    footer,
    showExampleSavings,
    exampleSavingsOptions,
    bottomLink,
    secondMiddle,
    secondCtaColor = ButtonColour.Secondary,
    TopResultComponent,
  } = props;

  const secondCtaText = secondCta?.title;
  const secondCtaUrl = secondCta?.url;
  const secondCtaHandleClick = secondCta?.handleClick;

  const [showPanel, setShowPanel] = useState(false);

  const middleSection = Object.entries(middle);
  const bottomSection = Object.entries(bottom);

  return (
    <div
      className="calculator-results__container"
      role="region"
      aria-label="Calculator results"
    >
      {
        TopResultComponent
          ? <TopResultComponent />
          : (
            <div className="calculator-results">
              <div className="calculator-results__content">
                {title && (
                //  eslint-disable-next-line jsx-a11y/no-redundant-roles
                <h2
                  className="calculator-results__heading"
                  role="heading"
                  aria-level={2}
                >
                  {title}
                </h2>
                )}
                <div className="calculator-results__total" data-testid="result">{result}</div>
                {subTitle && (
                <p className="calculator-results__more-text">{subTitle}</p>
                )}
                {showExampleSavings
            && getNumericValue(result.toString()) > 0
            && exampleSavingsOptions
            && exampleSavingsOptions.length > 0 && (
              <div className="exampleSavingsOptionsResults">
                <h2 className="exampleSavingsOptionsResults__title">
                  That's the same as
                </h2>
                <div className="optionResults">
                  {exampleSavingsOptions.map((option) => {
                    const {
                      isCurrency, cost, costSuffix, text, id,
                    } = option;
                    const costPerItem = getCostPerItem(
                      result.toString(),
                      cost,
                      isCurrency,
                    );
                    return (
                      getCostPerItem(result.toString(), cost, false) > 1 && (
                        <div className="optionResult" key={id}>
                          <div className="optionResult__heading">
                            {`${costPerItem} ${costSuffix}`}
                          </div>
                          <div className="optionResult__text">{text}</div>
                        </div>
                      )
                    );
                  })}
                </div>
              </div>
                )}

                <div
                  className={showPanel ? "calculator-results__show-panel" : undefined}
                  data-testid="calculator-results-show-panel"
                >
                  {!showExampleSavings && !exampleSavingsOptions && (
                  <HRule>
                    {middleSection.length > 0 && (
                    <button
                      title="Calculator results toggle panel button"
                      className="calculator-results__toggle-panel-btn"
                      onClick={() => {
                        setShowPanel(!showPanel);
                      }}
                      type="button"
                      data-testid="calculator-results-toggle-panel-button"
                    >
                      <img src={PanelPlusSVG} alt="plus" className="calculator-results__toggle-panel-btn__plus" loading="lazy" />
                      <img src={PanelMinusSVG} alt="minus" className="calculator-results__toggle-panel-btn__minus" loading="lazy" />
                    </button>
                    )}
                  </HRule>
                  )}

                  {middleSection.length > 0 && (
                  <>
                    <div className="calculator-results-up-to__section">
                      {middleSection.map((ms, index) => (
                        <div
                          key={`calc-mid-${index}`}
                          className="calculator-results-up-to__field"
                        >
                          <div className="calculator-results-up-to__label">
                            {ms[0]}
                            <span className="calculator-results-up-to__value">
                              {ms[1]}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <HRule cssClasses="horizontal-rule-2" />
                  </>
                  )}
                  {!!secondMiddle && (
                  <div className="calculator-results-second-middle">
                    <h3 className="calculator-results-second-middle__title">
                      {secondMiddle.secondaryTitle}

                    </h3>
                    <p className="calculator-results-second-middle__value" data-testid="secondary-result">
                      {secondMiddle.secondaryResult}
                    </p>
                  </div>
                  )}
                </div>

                <div className="calculator-results-fees">
                  {bottomTitle && (
                  <div className="calculator-results-fees__heading">
                    {bottomTitle}
                  </div>
                  )}
                  {bottomSection.map((bs, index) => (
                    <div
                      key={`calc-bottom-${index}`}
                      className="calculator-results-fees__field"
                    >
                      <div className="calculator-results-fees__label">
                        {bs[0]}
                        <span className="calculator-results-fees__value">{bs[1]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
      }
      <div className="calculator-results-next-steps">
        <div className="calculator-results__content">
          <h3 className="calculator-results-next-steps__heading">
            {footer.heading}
          </h3>
          {typeof footer.text === "string" ? (
            <div className="calculator-results-next-steps__text">
              {footer.text}
            </div>
          ) : (
            <RichText
              className="calculator-results-next-steps__text"
              content={footer.text}
            />
          )}
          <div className="calculator-results-next-steps__button">
            <CallToActionLink
              href={url}
              data-testid="first-cta"
              colour={ButtonColour.Primary}
              size={ButtonSize.Medium}
              onClick={ctaHandleClick}
            >
              {ctaText}
            </CallToActionLink>
            {secondCtaText && secondCtaUrl && (
              <CallToActionLink
                classOverrides={{ anchor: "calculatorSecondCta" }}
                href={secondCtaUrl}
                data-testid="second-cta"
                colour={secondCtaColor}
                size={ButtonSize.Medium}
                onClick={secondCtaHandleClick}
              >
                {secondCtaText}
              </CallToActionLink>
            )}
          </div>
        </div>
      </div>

      {
       bottomLink
       && (
       <>
         <HRule cssClasses="horizontal-rule-dark" />
         <p className="calculator-results__bottom-link">
           <Link className="tsl-link" to={bottomLink.url} {...bottomLink}>{bottomLink.title}</Link>
         </p>
       </>
       )
     }
    </div>
  );
};
